import { useEffect, useState } from 'react';
import {
  useCompanyQuery,
  useStripeCompanySubscriptionLazyQuery,
  useStripeCreateCustomerMutation,
  useStripeMeetingConcernsInvoicesLazyQuery,
} from '../../graphql/react-operations';
import { isNil } from 'lodash-es';
import { MeetingConcernQuote } from './MeetingConcernQuote';
import { SubPickerDialog } from './SubPickerDialog';
import { MeetingConcernInvoices } from './MeetingConcernInvoices';
import { type EventEmitter } from '@angular/core';
import { activeSubStatuses, needsPaymentSubStatuses } from '@startuptools/common/stripe';
import type { Stripe as StripeType } from 'stripe';
import { stripeProducts } from '../../../environments/environment';
import { LoadingDialog } from '../../components/simple-dialogs/LoadingDialog/LoadingDialog';
import { ConfirmDialog } from '@startuptools/ui/ConfirmDialog';
import { SubscriptionQuote } from './SubscriptionQuote';
import { type GqlStripeInvoiceStatus } from '../../graphql/base-types.graphql';

interface InnerProps {
  companyId: string;
  meetingId: string;
  onCancel: () => void;
  onPaid?: () => void;
  onBillingRedirect: () => void;
}

export function InnerStripe({ companyId, meetingId, onCancel, onPaid, onBillingRedirect }: InnerProps) {
  const company = useCompanyQuery({ variables: { id: companyId } });
  // eslint-disable-next-line sut/loading-mutation
  const [createCustomer, customerMut] = useStripeCreateCustomerMutation({ fetchPolicy: 'no-cache' });
  const [getCompanySub, companySubQuery] = useStripeCompanySubscriptionLazyQuery({
    variables: { companyId },
    fetchPolicy: 'no-cache',
  });
  const [getInvoices, invoicesQuery] = useStripeMeetingConcernsInvoicesLazyQuery({
    variables: { companyId, meetingId },
    fetchPolicy: 'no-cache',
  });

  const [selectedSub, setSelectedSub] = useState<{ priceId: string; prodId: string } | null>(null);

  useEffect(() => {
    if (company.loading || customerMut.loading || companySubQuery.loading || invoicesQuery.loading || !company.data) {
      return;
    }
    if (
      isNil(company.data.company.stripeCustomerId) &&
      !isNil(customerMut.data?.stripeCreateCustomer?.stripeCustomerId)
    ) {
      // Company needs to be refreshed if we just created the customer in stripe
      void company.refetch();
      return;
    }
    if (
      isNil(company.data.company.stripeCustomerId) &&
      isNil(customerMut.data?.stripeCreateCustomer?.stripeCustomerId)
    ) {
      void createCustomer({ variables: { companyId } });
      return;
    }
    if (!companySubQuery.called) {
      void getCompanySub();
    }
    if (!invoicesQuery.called) {
      void getInvoices({ variables: { companyId, meetingId } });
    }
  });

  if (
    company.loading ||
    customerMut.loading ||
    companySubQuery.loading ||
    invoicesQuery.loading ||
    isNil(company.data?.company.stripeCustomerId) ||
    isNil(companySubQuery?.data)
  ) {
    return <LoadingDialog />;
  }

  const handleOnCancel = () => {
    setSelectedSub(null);
    onCancel();
  };

  const handleOnPaid = async () => {
    // The company sub may have been updated (when creating a new one)
    await companySubQuery.refetch({ companyId }).then(() => {
      onPaid?.();
    });
    return;
  };

  const handleOnSubSelected = async (priceId: string, prodId: string) => {
    setSelectedSub({ priceId, prodId });
    return false;
  };

  const sub = companySubQuery?.data?.stripeCompanySubscription;
  if (isNil(sub) && isNil(selectedSub)) {
    return <SubPickerDialog open disableStartup onSubSelected={handleOnSubSelected} cancel={() => handleOnCancel()} />;
  }

  // User picked a plan
  if (isNil(sub) && !isNil(selectedSub)) {
    return (
      <SubscriptionQuote
        onCancel={handleOnCancel}
        onPaid={handleOnPaid}
        subPriceId={selectedSub.priceId}
        subProdId={selectedSub.prodId}
        {...{ companyId, meetingId }}
      />
    );
  }

  if (!isNil(sub)) {
    // TODO: refactor backend model to actually set a proper enum type for this
    if (needsPaymentSubStatuses.includes(sub.status as StripeType.Subscription.Status)) {
      // User has a subscription that needs to be paid before they can do anything else
      return (
        <ConfirmDialog
          header="Obetald prenumeration"
          confirmProps={{ children: 'Hantera betalning' }}
          onCancel={handleOnCancel}
          onConfirm={() => {
            handleOnCancel();
            onBillingRedirect();
          }}
        >
          <p>Företaget har en prenumeration som är obetald, betala den innan du kan fortsätta.</p>
        </ConfirmDialog>
      );
    }
    // If the company has a subscription, but it's not active, could be "paused"
    // TODO: refactor backend model to actually set a proper enum type for this
    if (!activeSubStatuses.includes(sub.status as StripeType.Subscription.Status)) {
      // TODO: contact support?
      return null;
    }
    const hasPro = sub.subscriptionItems.some(
      i => i.price.product === stripeProducts.pro || i.price.product === stripeProducts.proButler,
    );
    if (hasPro) {
      void handleOnPaid();
      return null;
    }
    const hasGrow = sub.subscriptionItems.some(i => i.price.product === stripeProducts.grow);
    const invoices = invoicesQuery.data?.stripeMeetingConcernsInvoices ?? [];
    const unpaidInvoices = invoices?.filter(i =>
      ['draft', 'open', 'uncollectible'].includes(i.status as GqlStripeInvoiceStatus),
    );
    if (hasGrow && unpaidInvoices.length > 0) {
      return (
        <MeetingConcernInvoices
          onCancel={() => handleOnCancel()}
          onPaid={() => handleOnPaid()}
          {...{ companyId, meetingId }}
        />
      );
    }
    if (hasGrow) {
      return (
        <MeetingConcernQuote
          onCancel={() => handleOnCancel()}
          onPaid={() => handleOnPaid()}
          {...{ companyId, meetingId }}
        />
      );
    }
    // It's not possible to upgrade and pay for program at the same time, tell them to upgrade first
    return (
      <ConfirmDialog
        header="Uppgradera prenumeration"
        confirmProps={{ children: 'Hantera prenumeration' }}
        onCancel={handleOnCancel}
        onConfirm={() => {
          handleOnCancel();
          onBillingRedirect();
        }}
      >
        <p>
          Med din nuvarande prenumeration kan du inte kan du inte skapa möten/optionsprogram/nyemissioner, uppgradera
          till GROW eller PRO.
        </p>
      </ConfirmDialog>
    );
  }
  return null;
}

interface Props extends Omit<InnerProps, 'onCancel' | 'onPaid' | 'onBillingRedirect'> {
  init: boolean;
  cancel: EventEmitter<void>;
  paid: EventEmitter<void>;
  billingRedirect: EventEmitter<void>;
}

export function Stripe({ init, companyId, meetingId, cancel, paid, billingRedirect }: Props) {
  if (!init) {
    return null;
  }
  return (
    <InnerStripe
      {...{ companyId, meetingId }}
      onCancel={() => {
        cancel.emit();
      }}
      onPaid={() => {
        paid.emit();
      }}
      onBillingRedirect={() => {
        billingRedirect.emit();
      }}
    />
  );
}
